import React, { useEffect, useMemo, useState } from "react";
import { Drawer, Table, Tooltip, notification } from "antd";
import type { ColumnsType } from "antd/es/table";
import Spinner from "../../../commons/Spinner/Spinner";
import useNotificationManagements from "../../../state/hooks/notifications-management/useNotificationManagements";
import { INotificationManagement } from "../../../models/Interface/NotificationManagement.interface";
import { getApiErrorMessage, groupDefinitionsByType } from "../helpers";
import {
  CountBadge,
  EllipsisCell,
  EmptyValue,
  MonoText,
  NotificationTableStyles,
  VariantBadge,
} from "./NotificationCells";

const TABLE_SCOPE = "notification-definitions-table";

/**
 * Most types own exactly one key set, so they render as a single row. Only a
 * rotating type earns a header row with its variants nested underneath —
 * grouping exists to stop variants reading as duplicates, and there is nothing
 * to disambiguate when there is only one.
 */
type RowKind = "single" | "group" | "variant";

interface DefinitionRow {
  key: string;
  kind: RowKind;
  notificationType: string;
  record?: INotificationManagement;
  variantCount?: number;
  variantRange?: string;
  children?: DefinitionRow[];
}

const ChevronIcon = ({ expanded }: { expanded: boolean }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    style={{
      transform: expanded ? "rotate(90deg)" : "none",
      transition: "transform 0.15s ease",
    }}
  >
    <path
      d="M9 6l6 6-6 6"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TypeName = ({ value }: { value: string }) => (
  <span
    className="text-dark-grey font-semibold"
    style={{
      fontFamily:
        "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      fontSize: 13,
      lineHeight: 1.45,
      overflowWrap: "anywhere",
    }}
  >
    {value}
  </span>
);

const DrawerRow = ({
  label,
  keyName,
  children,
}: {
  label: string;
  keyName?: string;
  children?: React.ReactNode;
}) => (
  <div className="flex flex-col gap-1 py-10 border-b border-light-grey-3 border-solid border-t-0 border-l-0 border-r-0">
    <span className="text-xs font-semibold" style={{ color: "#6b7280" }}>
      {label}
    </span>
    {children ?? (keyName ? <MonoText value={keyName} /> : <EmptyValue />)}
  </div>
);

const NotificationDefinitionsTable = () => {
  const { data, isLoading, isFetching, error } = useNotificationManagements();
  const definitions = useMemo(
    () => data?.notificationManagements ?? [],
    [data?.notificationManagements]
  );

  const [search, setSearch] = useState("");
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [detailRecord, setDetailRecord] =
    useState<INotificationManagement | null>(null);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return definitions;

    return definitions.filter((definition) =>
      [
        definition.notificationType,
        definition.titleKey,
        definition.descriptionKey,
        definition.description,
      ]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(term))
    );
  }, [definitions, search]);

  const groups = useMemo(() => groupDefinitionsByType(filtered), [filtered]);

  const rows: DefinitionRow[] = useMemo(
    () =>
      groups.map((group) => {
        const { notificationType, records } = group;

        if (records.length === 1) {
          return {
            key: records[0].id,
            kind: "single",
            notificationType,
            record: records[0],
          };
        }

        return {
          key: `type:${notificationType}`,
          kind: "group",
          notificationType,
          variantCount: records.length,
          variantRange: `${records[0].variant}–${
            records[records.length - 1].variant
          }`,
          children: records.map((record) => ({
            key: record.id,
            kind: "variant" as RowKind,
            notificationType,
            record,
          })),
        };
      }),
    [groups]
  );

  const stats = useMemo(() => {
    const totalTypes = groups.length;
    const rotatingTypes = groups.filter(
      (group) => group.records.length > 1
    ).length;
    return { totalTypes, rotatingTypes, totalKeySets: filtered.length };
  }, [groups, filtered.length]);

  // Rotating types are the reason this table is grouped — keep them open.
  useEffect(() => {
    setExpandedKeys(
      rows.filter((row) => row.kind === "group").map((row) => row.key)
    );
  }, [rows]);

  useEffect(() => {
    if (!error) return;
    notification.error({
      key: "notification-managements-load-error",
      message: "Error",
      description: getApiErrorMessage(
        error,
        "Failed to load notification definitions."
      ),
      placement: "top",
    });
  }, [error]);

  const columns: ColumnsType<DefinitionRow> = [
    {
      title: "Notification Type",
      key: "notificationType",
      width: 420,
      render: (_: unknown, row: DefinitionRow) => {
        if (row.kind === "variant") {
          return <VariantBadge variant={row.record?.variant ?? 1} />;
        }

        return (
          <div className="flex flex-col gap-1 min-w-0">
            <TypeName value={row.notificationType} />
            <div className="flex items-center gap-10">
              {row.kind === "group" ? (
                <CountBadge tone="accent">
                  {row.variantCount} key sets · variants {row.variantRange}
                </CountBadge>
              ) : (
                <VariantBadge variant={row.record?.variant ?? 1} subtle />
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: "Title Key",
      key: "titleKey",
      width: 280,
      ellipsis: true,
      render: (_: unknown, row: DefinitionRow) =>
        row.kind === "group" ? null : (
          <EllipsisCell value={row.record?.titleKey} mono />
        ),
    },
    {
      title: "Description",
      key: "description",
      render: (_: unknown, row: DefinitionRow) => {
        if (row.kind === "group") {
          return (
            <span className="text-sm" style={{ color: "#9CA3AF" }}>
              Rotating type — one key set is sent per user.
            </span>
          );
        }

        const description = row.record?.description;
        if (!description) return <EmptyValue />;

        return (
          <Tooltip title={description} placement="topLeft">
            <span
              className="text-sm text-dark-grey"
              style={
                {
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  lineHeight: 1.45,
                  opacity: 0.75,
                } as React.CSSProperties
              }
            >
              {description}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: 110,
      fixed: "right",
      align: "right",
      render: (_: unknown, row: DefinitionRow) =>
        row.kind === "group" ? null : (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setDetailRecord(row.record ?? null);
            }}
            className="text-xs font-semibold cursor-pointer whitespace-nowrap"
            style={{
              color: "#2d3ac7",
              background: "transparent",
              border: "none",
              padding: 0,
            }}
          >
            Details →
          </button>
        ),
    },
  ];

  const toggleGroup = (key: React.Key) =>
    setExpandedKeys((current) =>
      current.includes(key)
        ? current.filter((item) => item !== key)
        : [...current, key]
    );

  return (
    <div className="bg-white rounded-xl border border-light-grey-3 border-solid p-20 sm:p-30 w-full min-w-0 overflow-hidden">
      <div className="flex items-start justify-between mb-20 gap-20 flex-wrap">
        <div className="flex flex-col gap-10 min-w-0">
          <div className="flex items-center gap-10">
            <h3 className="text-dark-grey font-bold text-lg m-0">
              Notification Definitions
            </h3>
            {isFetching && !isLoading && <Spinner />}
          </div>
          <div className="flex items-center gap-10 flex-wrap">
            <CountBadge>
              {stats.totalTypes} type{stats.totalTypes === 1 ? "" : "s"}
            </CountBadge>
            <CountBadge>
              {stats.totalKeySets} key set{stats.totalKeySets === 1 ? "" : "s"}
            </CountBadge>
            {stats.rotatingTypes > 0 && (
              <CountBadge tone="accent">
                {stats.rotatingTypes} rotating
              </CountBadge>
            )}
          </div>
        </div>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter by type, key, or description..."
          className="rounded-md border border-light-grey-3 border-solid px-4 py-3 text-sm text-dark-grey
            focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          style={{ minWidth: 300 }}
          id="notification-definitions-search"
        />
      </div>

      <p className="text-sm text-dark-grey mb-20" style={{ opacity: 0.55 }}>
        A rotating type owns several key sets — those rows are its variants, not
        duplicates. Open Details for the full set of localization keys.
      </p>

      <div className={`w-full min-w-0 ${TABLE_SCOPE}`}>
        <Table<DefinitionRow>
          dataSource={rows}
          columns={columns}
          rowKey="key"
          loading={isLoading}
          size="middle"
          tableLayout="fixed"
          scroll={{ x: 1000 }}
          pagination={false}
          rowClassName={(row) =>
            `notification-row-clickable ${
              row.kind === "group" ? "notification-type-row" : ""
            }`
          }
          onRow={(row) => ({
            onClick: () => {
              if (row.kind === "group") {
                toggleGroup(row.key);
                return;
              }
              setDetailRecord(row.record ?? null);
            },
          })}
          expandable={{
            expandedRowKeys: expandedKeys,
            onExpandedRowsChange: (keys) => setExpandedKeys([...keys]),
            expandIcon: ({ expanded, onExpand, record, expandable }) =>
              expandable ? (
                <button
                  type="button"
                  aria-label={expanded ? "Collapse type" : "Expand type"}
                  onClick={(event) => {
                    event.stopPropagation();
                    onExpand(record, event);
                  }}
                  className="notification-expand-icon"
                >
                  <ChevronIcon expanded={expanded} />
                </button>
              ) : (
                <span className="notification-expand-spacer" />
              ),
          }}
          locale={{
            emptyText: search
              ? "No notification definitions match this filter."
              : "No notification definitions found.",
          }}
        />
        <NotificationTableStyles scope={TABLE_SCOPE} />
        <style>{`
          .${TABLE_SCOPE} .notification-row-clickable { cursor: pointer; }
          .${TABLE_SCOPE} .notification-expand-icon,
          .${TABLE_SCOPE} .notification-expand-spacer {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 22px;
            height: 22px;
            vertical-align: middle;
          }
          .${TABLE_SCOPE} .notification-expand-icon {
            background: #fff;
            border: 1px solid #E6E8EF;
            border-radius: 6px;
            color: #2d3ac7;
            cursor: pointer;
            padding: 0;
          }
          .${TABLE_SCOPE} .notification-expand-icon:hover {
            background: #F0F1FB;
            border-color: #C7CBF0;
          }
        `}</style>
      </div>

      <Drawer
        title="Notification Definition"
        open={!!detailRecord}
        onClose={() => setDetailRecord(null)}
        width={460}
      >
        {detailRecord && (
          <div className="flex flex-col">
            <DrawerRow label="Notification type">
              <div className="flex flex-col gap-10 items-start">
                <TypeName value={detailRecord.notificationType} />
                <VariantBadge variant={detailRecord.variant} />
              </div>
            </DrawerRow>
            <DrawerRow label="Title key" keyName={detailRecord.titleKey} />
            <DrawerRow
              label="Description key"
              keyName={detailRecord.descriptionKey}
            />
            <DrawerRow label="Image URL key" keyName={detailRecord.urlKey} />
            <DrawerRow label="Fallback key" keyName={detailRecord.fallbackKey} />
            <DrawerRow label="Deeplink key" keyName={detailRecord.deeplinkKey} />
            <DrawerRow label="Description">
              <span
                className="text-sm text-dark-grey"
                style={{ wordBreak: "break-word", lineHeight: 1.45 }}
              >
                {detailRecord.description || "—"}
              </span>
            </DrawerRow>
            <DrawerRow label="Record ID">
              <MonoText value={detailRecord.id} />
            </DrawerRow>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default NotificationDefinitionsTable;
